/**
 * Kai Styleguide (2026) design tokens.
 *
 * Sources of truth:
 *   - Kai Styleguide (Kai_Styleguide.html) — original 7-color palette
 *   - Project_Comprehensive.html (Quinn, 2026-07-02) — extended palette,
 *     control sizing, typography refinement for the full project lifecycle
 *     shell. This is the reference implementation for the company-wide reskin.
 *
 * Coexists with the existing figmaColorScales / figmaSemanticColors during the
 * proving phase — migrated components consume these tokens, un-migrated
 * components keep the old figma tokens until they follow.
 *
 * Palette: 7 named colors + extended steps.
 *   ink            #1A1A1A  primary text, heavy borders, dark chrome (top bar)
 *   white          #FFFFFF  highest-contrast surfaces (cards, modals, tiles)
 *   paper          #FFFDF8  app background (warm)
 *   stroke         #DDD6C8  borders, hairlines, divider lines
 *   midtone        #7D7469  completed-step indicators, muted icons
 *   accent         #FDFF86  primary CTA fill, active/selected emphasis
 *   error          #C0443A  destructive actions, warnings, attention
 *
 * Extended palette (from Project_Comprehensive.html):
 *   accentDeep     #EDEF60  accent hover state (primary buttons)
 *   success        #4A8C5C  publish / approved / positive confirmation
 *   sunken         #F4F0E6  muted surface — hover regions, popover backgrounds
 *   textSecondary  #5C5750  secondary body text (descriptions, modal body)
 *   textTertiary   #8A8470  tertiary text — labels, captions, muted metadata
 *
 * Derived (minimal):
 *   surfaceMuted   slightly off-paper for muted regions
 *   strokeFaded    lighter stroke for placeholders / disabled edges
 *   midtoneFaded   faded midtone for placeholder / disabled text
 */

/** The 7-color styleguide palette. */
export const styleguideColors = {
  ink: '#1A1A1A',
  white: '#FFFFFF',
  paper: '#FFFDF8',
  stroke: '#DDD6C8',
  midtone: '#7D7469',
  accent: '#FDFF86',
  error: '#C0443A',
} as const;

/**
 * Applies an alpha channel to a styleguide color token — e.g. the
 * translucent-white borders/text the stage stepper uses on the ink top bar
 * (`withOpacity(styleguideColors.white, 0.35)`) — instead of hardcoding a
 * second, disconnected rgba literal for a color that's already a token.
 */
export function withOpacity(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '');
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Extended palette — colors added in the Project Comprehensive mockup. */
export const styleguideColorsExtended = {
  ...styleguideColors,
  /** Accent hover state for primary buttons (#EDEF60). */
  accentDeep: '#EDEF60',
  /** Success / approved / published (#4A8C5C). */
  success: '#4A8C5C',
  /** Muted surface — hover regions, popover bgs, sunken panels (#F4F0E6). */
  sunken: '#F4F0E6',
  /** Secondary body text — descriptions, modal body (#5C5750). */
  textSecondary: '#5C5750',
  /** Tertiary text — labels, captions, muted metadata (#8A8470). */
  textTertiary: '#8A8470',
  /** Slightly off-paper for hover / muted surface regions. */
  surfaceMuted: '#FAF8F2',
  /** Lighter stroke for placeholder / disabled edges. */
  strokeFaded: '#E4DFD3',
  /** Faded midtone for placeholder / disabled text. */
  midtoneFaded: '#B0A89E',
  /** Selected-row wash — lemon tint for highlighted rows/tasks. */
  wash: '#FDFDEA',
} as const;

/**
 * Radius. The styleguide is almost entirely sharp-cornered: only `50%` (status
 * dots, avatars) and one control-specific toggle exception survive. No
 * intermediate radii.
 */
export const styleguideRadius = {
  /** Default for all surfaces, cards, tiles, buttons, markers. */
  sharp: 0,
  /** Status dots, avatars, group-color dots. */
  circle: '50%',
} as const;

/**
 * Spacing scale (4px base). Replaces ad-hoc pixel padding in migrated
 * components.
 */
export const styleguideSpacing = {
  s1: 4,
  s2: 8,
  s3: 12,
  s4: 16,
  s5: 24,
  s6: 32,
  s7: 48,
  s8: 64,
} as const;

/**
 * Typography. Three families from the styleguide: Maxeville (display),
 * Circular XX (body — already loaded via circularXX.css), Maxeville Construct
 * (mono labels). Sizes/weights/letter-spacing per the styleguide type grid.
 */
export const styleguideFontFamily = {
  /** Page titles, section headings, address bars, document titles.
   *  Space Grotesk is the Google Fonts fallback when Maxeville isn't loaded. */
  display: 'Maxeville, "Space Grotesk", "Helvetica Neue", sans-serif',
  /** Body copy, item descriptions, numeric amounts.
   *  Hanken Grotesk is the Google Fonts fallback when Circular XX isn't loaded. */
  body: '"Circular XX", CircularXX, "Hanken Grotesk", "Helvetica Neue", sans-serif',
  /** Labels, page tabs, buttons, status pills, metadata.
   *  Space Mono is the Google Fonts fallback when Maxeville Construct isn't loaded. */
  mono: '"Maxeville Construct", "Space Mono", Maxeville, monospace',
} as const;

export const styleguideFontSize = {
  /** Display · Large (28). */
  displayLarge: 28,
  /** Display · Medium (24). */
  displayMedium: 24,
  /** Display · Modal title (20). */
  displayModalTitle: 20,
  /** Display · Address bar / preset name (14). */
  displayAddress: 14,
  /** Display · Small (18). */
  displaySmall: 18,
  /** Body · Large (16). */
  bodyLarge: 16,
  /** Body · Medium (13.5) — modal body, descriptions. */
  bodyMedium: 13.5,
  /** Body · Default (15) — standard interface text. */
  bodyDefault: 15,
  /** Body · Small (12.5) — helper text, captions. */
  bodySmall: 12.5,
  /** Body · Micro (11.5) — stage captions, popover labels. */
  bodyMicro: 11.5,
  /** Mono · Button / label (10.5). */
  monoLabel: 10.5,
  /** Mono · Stage number (11). */
  monoStageNum: 11,
  /** Mono · Caption / metadata (10). */
  monoCaption: 10,
  /** Mono · Micro (9.5) — top bar metadata, section headers. */
  monoMicro: 9.5,
} as const;

export const styleguideFontWeight = {
  regular: 400,
  bold: 700,
} as const;

/** Letter-spacing for mono labels (per Quinn's mockup: button 0.06em, caption 0.08em, header 0.12em). */
export const styleguideLetterSpacing = {
  /** Buttons, role labels, action labels. */
  monoLabel: '0.06em',
  /** Stage names, address metadata, status text. */
  monoTight: '0.08em',
  /** Section headers, fieldset legends, micro captions. */
  monoCaption: '0.12em',
} as const;

/**
 * Control sizing — structural constants extracted from Quinn's Project
 * Comprehensive mockup. These give squads a common starting point for the
 * chrome reskin without needing to reverse-engineer the mockup CSS.
 */
export const styleguideControlSizes = {
  /** Top bar height (px). Collapsible to 0 via the tb-hidden class. */
  topBarHeight: 56,
  /** Top bar horizontal padding. */
  topBarPaddingX: 20,
  /** Brand mark box width (`.tb-mark`) — fixed, flush against the top bar's
   *  left edge; height matches topBarHeight. */
  brandMarkWidth: 72,
  /** Button vertical padding (primary, ghost, danger). */
  buttonPaddingY: 9,
  /** Button horizontal padding. */
  buttonPaddingX: 16,
  /** Button border width — Quinn uses 1.5px for all button variants. */
  buttonBorderWidth: 1.5,
  /** Compact button padding (action bar secondary actions). */
  buttonCompactPaddingY: 6,
  buttonCompactPaddingX: 12,
  /** Stage stepper number/hexagon marker box. */
  stageNumberSize: 24,
  /** Stage stepper vertical padding. */
  stagePaddingY: 14,
  /** Stage stepper horizontal inset — it renders inside ProjectTopBar's ink
   *  bar (`.app-toolbar .stages` in Project_Comprehensive.html), not on its
   *  own surface, so this is padding from the bar's edges/siblings. */
  stagePaddingX: 22,
  /** Gap between a stage's number circle and its label. */
  stageItemGap: 9,
  /** Gap between stages — Project_Comprehensive_ShopEdit.html centers the
   *  stepper as a tight cluster instead of stretching stages edge to edge,
   *  so spacing is a container `gap` rather than per-stage padding. */
  stageClusterGap: 40,
  /** Action bar height (slim secondary action row). */
  actionBarPaddingY: 6,
  actionBarPaddingX: 20,
} as const;

/** Elevation shadows from Quinn's mockup. */
export const styleguideElevation = {
  /** Popovers, role menus, settings dropdowns. */
  popover: '0 10px 26px rgba(26, 26, 26, 0.18)',
  /** Modal dialog. */
  modal: '0 24px 64px rgba(26, 26, 26, 0.3)',
  /** Card / surface (subtle). */
  card: '0 2px 8px rgba(0, 0, 0, 0.05)',
} as const;

/** Border-width scale. Quinn's mockup uses two weights consistently. */
export const styleguideBorderWidth = {
  /** Hairline borders, dividers, card edges (1px). */
  thin: 1,
  /** Control borders — buttons, inputs, checkboxes, stage circles (1.5px). */
  control: 1.5,
} as const;

/**
 * Attention dot palette — semantic indicator colors for task attention flags.
 * Used as 7px dots on task rows and in detail panels.
 * Source: ProjectReview_ScopePanel.html (Quinn).
 */
export const styleguideAttentionColors = {
  /** Unassigned contractor. */
  unassigned: '#9E6B2F',
  /** Deferred work item. */
  deferred: '#6B5B8A',
  /** Tenant responsible. */
  tenant: '#2E5A87',
  /** Missing product. */
  missing: '#C0443A',
  /** Out of stock. */
  outOfStock: '#C4960F',
  /** Financial / informational. */
  financial: '#1A1A1A',
} as const;

/**
 * Task status pill colors — background + text pairs for each lifecycle state.
 * Source: ProjectReview_ScopePanel.html (Quinn).
 */
export const styleguideTaskStatusColors = {
  notStarted: { text: '#8A8470', bg: 'transparent', border: '#DDD6C8' },
  inProgress: { text: '#9E6B2F', bg: '#FBF1E3', border: 'transparent' },
  pending: { text: '#5C5750', bg: '#F4F0E6', border: 'transparent' },
  inReview: { text: '#2E5A87', bg: '#E9F0F6', border: 'transparent' },
  complete: { text: '#4A8C5C', bg: '#EAF3ED', border: 'transparent' },
} as const;
